import React, { Component, useEffect } from 'react'
import { Form, Formik } from 'formik'
import type { IntlShape } from 'react-intl'
import { FormattedMessage, injectIntl } from 'react-intl'
import styled from 'styled-components'
import { ButtonMedium } from 'app/assets/styles'
import { colors } from 'shared/assets/styles'
import { CaptionText, Text, TinyText } from 'app/common/typography'
import { Link } from 'app/common/link'
import { CheckOrRadio, Error, Input, Password } from 'app/common/forms'
import { boolean, object, string } from 'yup'
import { Checkbox } from 'app/assets/icons'
import type { LoginForm } from 'shared/types'
import { Redirect } from 'react-router-dom'
import { SocialConnect } from 'app/common/social-connect'
import { errors, featureCodes, messages } from 'shared/utils'
import { FeatureToggle } from '../../common/feature-toggle'
import { Recaptcha } from 'app/common/recaptcha'
import { Loader } from 'app/common/loader'
import type { State as SigninPageState } from 'shared/store/signin'
import { sendCodeMutation } from '../mfa/useMFAMutations'
import { useSelector } from 'react-redux'
import type { RootState } from 'shared/store/reducers'

const { red, grey, yellow } = colors

type Props = {
    signin: (LoginForm, ?boolean) => Promise<?$Values<typeof messages>>,
    goToRedirectAndReset: (url: string) => void,
    resendMail: (queen: string) => void,
    fromUrl?: string,
    fromEcommerce?: boolean,
    success: boolean,
    redirectTo: ?string,
    sendFormError?: string,
    intl: IntlShape
} & SigninPageState

type State = {
    showCaptcha: boolean
}

class Signin extends Component<Props, State> {
    recaptchaPromise: Promise<string>
    resolve: any
    reject: any

    constructor() {
        super()
        this.state = {
            showCaptcha: false
        }
        this.initRecaptchaPromise()
    }

    initRecaptchaPromise = () => {
        this.recaptchaPromise = new Promise((resolve, reject) => {
            this.resolve = resolve
            this.reject = reject
        })
    }

    verifyCallback = (recaptchaToken: string) => {
        if (!!recaptchaToken) {
            this.resolve(recaptchaToken)
        } else {
            this.reject('Error getting recaptcha token')
        }
    }

    formSubscription = () => {
        const mandatory = 'form.error.field.mandatory'
        return object().shape({
            email: string().required(mandatory),
            password: string().required(mandatory),
            stayLoggedIn: boolean().required(mandatory)
        })
    }

    handleSubmit = async (values: any, actions: any) => {
        const { fromEcommerce, signin, intl } = this.props
        try {
            this.setState({ showCaptcha: true })
            const recaptchaToken = await this.recaptchaPromise
            const data = {
                stayLoggedIn: values.stayLoggedIn,
                login: values.email.trim(),
                password: values.password,
                recaptchaToken
            }
            const result = await signin(data, fromEcommerce)

            if (result.message === messages.MFA_REQUIRED) {
                await sendCodeMutation(result.mfaToken)
                this.props.goToRedirectAndReset(
                    intl.formatMessage({ id: 'routes.mfa.confirm.code' }),
                    { redirectTo: this.props.redirectTo }
                )
            }
            if (
                result.message === messages.OK &&
                this.props.redirectToAfterLogin
            ) {
                this.props.goToRedirectAndReset(this.props.redirectToAfterLogin)
            }
        } finally {
            this.setState({ showCaptcha: false })
            this.initRecaptchaPromise()
            if (actions) {
                actions.setSubmitting(false)
            }
        }
    }

    render() {
        const {
            success,
            resendMail,
            fromUrl,
            sendFormError,
            redirectTo,
            intl
        } = this.props
        const { showCaptcha } = this.state
        const initialValues: any = {
            email: '',
            password: '',
            stayLoggedIn: false
        }
        const routeDashboard = intl.formatMessage({
            id: 'routes.kingdom.dashboard'
        })

        const accountInactive = errors.ACCOUNT_INACTIVE

        return success ? (
            <CustomRedirect to={redirectTo ? redirectTo : routeDashboard} />
        ) : (
            <Formik
                initialValues={initialValues}
                enableReinitialize={true}
                onSubmit={this.handleSubmit}
                validationSchema={this.formSubscription()}>
                {({ values, errors, touched, isSubmitting, setFieldValue }) => (
                    <Form>
                        {showCaptcha && (
                            <Recaptcha
                                action="WebSignin"
                                verifyCallback={this.verifyCallback}
                            />
                        )}
                        <FeatureToggle feature={featureCodes.AUTHENT}>
                            <SocialConnect
                                redirectUrl={
                                    fromUrl
                                        ? fromUrl
                                        : redirectTo
                                        ? redirectTo
                                        : intl.formatMessage({
                                              id: 'routes.kingdom.dashboard'
                                          })
                                }
                                fromLogin
                                fromEcommerce
                            />
                        </FeatureToggle>
                        <FeatureToggle features={featureCodes.AUTHENT_CLASSIC}>
                            <Input
                                name="email"
                                type="email"
                                labelKey="signin.form.email.title"
                                placeholderKey="form.email.placeholder"
                                className={
                                    errors.email && touched.email ? 'error' : ''
                                }
                            />
                            {errors.email && touched.email && (
                                <Error errorKey={errors.email} />
                            )}
                            <Password
                                name="password"
                                type="password"
                                labelKey="signin.form.password.title"
                                placeholderKey="signin.form.password.placeholder"
                                className={
                                    errors.password && touched.password
                                        ? 'error'
                                        : ''
                                }
                            />
                            {errors.password && touched.password && (
                                <Error errorKey={errors.password} />
                            )}
                            <Paragraph>
                                <ResetPassword
                                    url={intl.formatMessage({
                                        id: 'routes.reset.password'
                                    })}
                                    params={
                                        fromUrl
                                            ? {
                                                  authenticationIsFromUrl: fromUrl
                                              }
                                            : {}
                                    }>
                                    <FormattedMessage id="signin.reset.password">
                                        {(text: string) => (
                                            <CaptionText color={grey}>
                                                {text}
                                            </CaptionText>
                                        )}
                                    </FormattedMessage>
                                </ResetPassword>
                            </Paragraph>
                            <CheckMention
                                onClick={() =>
                                    setFieldValue(
                                        'stayLoggedIn',
                                        !values.stayLoggedIn
                                    )
                                }>
                                <CheckOrRadio
                                    name="stayLoggedIn"
                                    type="checkbox"
                                    labelKey="signin.form.keep.logged.in"
                                    checked={values.stayLoggedIn}>
                                    <Checkbox checked={values.stayLoggedIn} />
                                </CheckOrRadio>
                            </CheckMention>
                            {sendFormError && (
                                <Mentions>
                                    <FormattedMessage
                                        id={
                                            sendFormError === accountInactive
                                                ? 'signin.form.error.account.inactive'
                                                : sendFormError
                                        }>
                                        {(text: string) => (
                                            <Text color={red}>{text}</Text>
                                        )}
                                    </FormattedMessage>
                                </Mentions>
                            )}
                            {sendFormError === accountInactive && (
                                <FormattedMessage id="signin.form.error.account.inactive.resend">
                                    {(text: string) => (
                                        <ResendMail
                                            onClick={async () => {
                                                try {
                                                    this.setState({
                                                        showCaptcha: true
                                                    })
                                                    const recaptchaToken = await this
                                                        .recaptchaPromise
                                                    resendMail(recaptchaToken)
                                                } finally {
                                                    this.setState({
                                                        showCaptcha: false
                                                    })
                                                    this.initRecaptchaPromise()
                                                }
                                            }}>
                                            {text}
                                        </ResendMail>
                                    )}
                                </FormattedMessage>
                            )}
                            <Loader loading={isSubmitting} />
                            <ButtonContainer>
                                <StyledButtonMedium
                                    type="submit"
                                    background={yellow}
                                    disabled={isSubmitting}>
                                    <FormattedMessage id="signup.login.button" />
                                </StyledButtonMedium>
                            </ButtonContainer>
                            <Mentions>
                                <FormattedMessage id="form.mandatory.information">
                                    {(text: string) => (
                                        <TinyText color={grey}>{text}</TinyText>
                                    )}
                                </FormattedMessage>
                            </Mentions>
                        </FeatureToggle>
                    </Form>
                )}
            </Formik>
        )
    }
}

const CustomRedirect = ({ to }: { to: string }) => {
    const newOrderFlowEnabled = useSelector((s: RootState) =>
        s.featuresState.available.includes(featureCodes.ENABLE_NEW_ORDER_FLOW)
    )

    useEffect(() => {
        if (newOrderFlowEnabled) {
            document.location.href = to
        }
    }, [to, newOrderFlowEnabled])

    if (!newOrderFlowEnabled) {
        return <Redirect to={to} />
    }

    return null
}

const Mentions = styled.div`
    margin-top: 25px;
    margin-bottom: 25px;
`

const CheckMention = styled.div`
    width: 100%;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex-direction: row;
    margin: 20px 0 10px;
`

const ButtonContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 30px 0;
`

const StyledButtonMedium = styled(ButtonMedium)`
    align-self: center;
    border: none;
`

const Paragraph = styled.p``

const ResetPassword = styled(Link)`
    cursor: pointer;
    text-decoration: underline;
    margin: 10px 0;
`

const ResendMail = styled(Text)`
    cursor: pointer;
    text-decoration: underline;
    margin: 10px 0;
    color: #bb1e32;
`

const SigninFormComponent = injectIntl(Signin)

export { SigninFormComponent }
